import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import {
  acceptProsRequest,
  declineProsRequest,
  getProsRequestDetail,
  sendProsQuoteDraft,
  upsertProsQuoteDraft,
  type ProRequestDetail,
} from '../api/prosRequests'

function toStatusTone(status?: string) {
  const key = String(status || '').toUpperCase()
  if (key === 'PENDING') return 'border-amber-400/35 bg-amber-500/10 text-amber-100'
  if (key === 'CLAIMED') return 'border-emerald-400/35 bg-emerald-500/10 text-emerald-100'
  if (key === 'DECLINED') return 'border-white/20 bg-white/10 text-white/75'
  if (key === 'EXPIRED') return 'border-red-400/30 bg-red-500/10 text-red-200'
  return 'border-white/20 bg-white/5 text-white/70'
}

export default function ProsRequestDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<ProRequestDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [amountPixe, setAmountPixe] = useState<string>('')
  const [description, setDescription] = useState<string>('')

  const isPending = String(detail?.status || '').toUpperCase() === 'PENDING'
  const isClaimed = String(detail?.status || '').toUpperCase() === 'CLAIMED'
  const canSaveQuote = isPending || isClaimed
  const canSendQuote = canSaveQuote && Number(amountPixe || '0') > 0 && description.trim().length > 0

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getProsRequestDetail(id)
      .then((data) => {
        if (cancelled) return
        setDetail(data)
        setAmountPixe(data?.quote_draft?.amount_pixe != null ? String(data.quote_draft.amount_pixe) : '')
        setDescription(String(data?.quote_draft?.description || ''))
      })
      .catch((e: any) => {
        if (cancelled) return
        setError(e?.response?.data?.detail || e?.message || 'Failed to load request detail.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  const statusLabel = useMemo(() => {
    const status = String(detail?.status || '')
    return status ? status[0] + status.slice(1).toLowerCase() : 'Unknown'
  }, [detail?.status])

  async function runAction(kind: 'accept' | 'decline') {
    setSaving(kind)
    setError(null)
    setNotice(null)
    try {
      const next = kind === 'accept' ? await acceptProsRequest(id) : await declineProsRequest(id)
      setDetail(next)
      setNotice(kind === 'accept' ? 'Request accepted.' : 'Request declined.')
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setError('Another pro already claimed this request.')
      } else {
        setError(e?.response?.data?.detail || e?.message || `Failed to ${kind} request.`)
      }
    } finally {
      setSaving(null)
    }
  }

  async function onSaveQuote() {
    setSaving('quote')
    setError(null)
    setNotice(null)
    const parsed = amountPixe.trim() ? Number(amountPixe.trim()) : null
    try {
      await upsertProsQuoteDraft(id, {
        amount_pixe: parsed != null && Number.isFinite(parsed) ? Math.max(1, Math.round(parsed)) : null,
        description,
      })
      const refreshed = await getProsRequestDetail(id)
      setDetail(refreshed)
      setNotice('Quote saved.')
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save quote draft.')
    } finally {
      setSaving(null)
    }
  }

  async function onSendQuote() {
    setSaving('send_quote')
    setError(null)
    setNotice(null)
    const parsed = amountPixe.trim() ? Number(amountPixe.trim()) : null
    try {
      await upsertProsQuoteDraft(id, {
        amount_pixe: parsed != null && Number.isFinite(parsed) ? Math.max(1, Math.round(parsed)) : null,
        description,
      })
      const result = await sendProsQuoteDraft(id)
      setNotice('Quote sent.')
      if (result?.job_id) navigate(`/jobs/${result.job_id}`)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to send quote.')
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-6xl space-y-4 px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white hover:bg-white/10"
          >
            Back
          </button>
          <Link to="/settings/pixe-pros?tab=requests" className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10">
            Requests Inbox
          </Link>
        </div>

        {loading ? <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">Loading request detail...</div> : null}
        {error ? <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div> : null}
        {notice ? <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">{notice}</div> : null}

        {detail ? (
          <>
            <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h1 className="text-xl font-bold text-white">{detail.job?.title || 'Request'}</h1>
                <span className={`rounded-full border px-2.5 py-1 text-xs ${toStatusTone(detail.status)}`}>{statusLabel}</span>
              </div>
              <div className="mt-2 text-xs text-white/65">
                {detail.type === 'invite' ? 'Invite' : 'Delivery request'} from {detail.requested_by?.display_name || detail.requested_by?.handle || 'Client'}
              </div>
            </section>

            <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
              <div className="space-y-4">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-sm font-semibold text-white">Request details</div>
                  <div className="mt-3 grid gap-2 text-sm text-white/75">
                    <div><span className="text-white/55">Type:</span> {detail.type}</div>
                    <div><span className="text-white/55">Mode:</span> {detail.job?.mode || '-'}</div>
                    <div><span className="text-white/55">Location:</span> {detail.job?.location || '-'}</div>
                    <div><span className="text-white/55">Budget hint:</span> {detail.job?.budget_hint || '-'}</div>
                    <div><span className="text-white/55">Created:</span> {detail.job?.created_at ? new Date(detail.job.created_at).toLocaleString() : '-'}</div>
                  </div>
                  <div className="mt-4 text-sm text-white/85 whitespace-pre-wrap">{detail.job?.description || 'No description provided.'}</div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-sm font-semibold text-white">Message client (coming soon)</div>
                  <textarea
                    disabled
                    rows={4}
                    placeholder="Messaging will be enabled in a follow-up slice."
                    className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white/70 opacity-70"
                  />
                </div>
              </div>

              <aside className="space-y-4">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-sm font-semibold text-white">Actions</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void runAction('accept')}
                      disabled={!isPending || Boolean(saving)}
                      className="rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {saving === 'accept' ? 'Working...' : 'Accept'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void runAction('decline')}
                      disabled={!isPending || Boolean(saving)}
                      className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {saving === 'decline' ? 'Working...' : 'Decline'}
                    </button>
                    {isClaimed ? (
                      <button
                        type="button"
                        disabled
                        title="Slice 3"
                        className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white/50"
                      >
                        Mark In Progress (Slice 3)
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-sm font-semibold text-white">Quote draft</div>
                  <label className="mt-3 block text-xs text-white/70">Amount (PIXE)</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={amountPixe}
                    onChange={(e) => setAmountPixe(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white"
                    placeholder="50"
                  />
                  <div className="mt-1 text-[11px] text-white/50">PIXE is Pixeloop credit. Clients pay pros in PIXE.</div>

                  <label className="mt-3 block text-xs text-white/70">Description</label>
                  <textarea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white"
                    placeholder="Scope, timeline, and deliverables"
                  />

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void onSaveQuote()}
                      disabled={!canSaveQuote || Boolean(saving)}
                      className="rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {saving === 'quote' ? 'Saving...' : 'Save Quote'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void onSendQuote()}
                      disabled={!canSendQuote || Boolean(saving)}
                      className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Send Quote
                    </button>
                  </div>
                </div>
              </aside>
            </section>
          </>
        ) : null}
      </main>
    </div>
  )
}
